import type { LineSeriesOption } from 'echarts'
import { thaloColors } from '@/styles/thalo-colors'

// ── Types ─────────────────────────────────────────────────────────────────────

export type TooltipVisibility =
  | 'show'       // appears with name and value
  | 'name-only'  // appears with name only (e.g. trendlines)
  | 'hidden'     // filtered out entirely (e.g. internal rendering series)

export interface SeriesStyleContext {
  /** Position of this series among all series in its result response */
  seriesIndex: number
  /** Position of this series among trendline series only (for color cycling) */
  trendlineIndex: number
}

export interface SeriesStyleOverrides {
  lineStyle?: LineSeriesOption['lineStyle']
  itemStyle?: LineSeriesOption['itemStyle']
}

export interface SeriesTypeDefinition {
  readonly kind: string
  /** Returns true if a series with the given name belongs to this type */
  matches: (name: string) => boolean
  /** How this series appears in the hover tooltip */
  tooltipVisibility: TooltipVisibility
  /** Whether gap detection should skip this series */
  skipGapDetection: boolean
  /** Whether to suppress point symbols regardless of the chart's showPoints setting */
  symbolAlwaysHidden: boolean
  /** Returns line and item style overrides for this series type */
  resolveStyle: (context: SeriesStyleContext) => SeriesStyleOverrides
  /** Returns the area fill style for this series type */
  resolveAreaStyle: () => LineSeriesOption['areaStyle']
}

// ── Series type definitions ───────────────────────────────────────────────────

const outdoorTemperature: SeriesTypeDefinition = {
  kind: 'outdoor-temperature',
  matches: name => name.includes('Outdoor Temperature'),
  tooltipVisibility: 'show',
  skipGapDetection: true,
  symbolAlwaysHidden: true,
  resolveStyle: () => ({
    lineStyle: { color: thaloColors.accent.peach },
    itemStyle: { color: thaloColors.accent.peach },
  }),
  resolveAreaStyle: () => ({ opacity: 0.03 }),
}

const trendline: SeriesTypeDefinition = {
  kind: 'trendline',
  matches: name => name.includes('Trend'),
  tooltipVisibility: 'name-only',
  skipGapDetection: true,
  symbolAlwaysHidden: false,
  resolveStyle: ({ trendlineIndex }) => {
    const colorKeys = Object.keys(thaloColors.secondary) as Array<keyof typeof thaloColors.secondary>
    const color = thaloColors.secondary[colorKeys[trendlineIndex % colorKeys.length]]
    return {
      lineStyle: { color, type: 'dashed', width: 2.5, opacity: 0.6, dash: [8, 2] },
      itemStyle: { color, opacity: 0.2 },
    }
  },
  resolveAreaStyle: () => ({ opacity: 0.03 }),
}

const gapBridge: SeriesTypeDefinition = {
  kind: 'gap-bridge',
  matches: name => name.includes('(gap)'),
  tooltipVisibility: 'hidden',
  skipGapDetection: true,
  symbolAlwaysHidden: true,
  resolveStyle: () => ({}),
  resolveAreaStyle: () => ({ opacity: 0 }),
}

const powerOffBand: SeriesTypeDefinition = {
  kind: 'power-off-band',
  matches: name => name === '__power_off_bands',
  tooltipVisibility: 'hidden',
  skipGapDetection: true,
  symbolAlwaysHidden: true,
  resolveStyle: () => ({}),
  resolveAreaStyle: () => ({ opacity: 0 }),
}

const idealHighUp: SeriesTypeDefinition = {
  kind: 'ideal-high-up',
  matches: name => name === 'ideal_high_up',
  tooltipVisibility: 'show',
  skipGapDetection: false,
  symbolAlwaysHidden: false,
  resolveStyle: ({ seriesIndex }) => {
    const colorKeys = Object.keys(thaloColors.primary) as Array<keyof typeof thaloColors.primary>
    const color = thaloColors.primary[colorKeys[seriesIndex % colorKeys.length]]
    return { lineStyle: { color }, itemStyle: { color } }
  },
  resolveAreaStyle: () => ({
    origin: 'end' as const,
    color: thaloColors.primary.deepNavy,
    opacity: 0.03,
  }),
}

const idealHighDown: SeriesTypeDefinition = {
  kind: 'ideal-high-down',
  matches: name => name === 'ideal_high_down',
  tooltipVisibility: 'show',
  skipGapDetection: false,
  symbolAlwaysHidden: false,
  resolveStyle: ({ seriesIndex }) => {
    const colorKeys = Object.keys(thaloColors.primary) as Array<keyof typeof thaloColors.primary>
    const color = thaloColors.primary[colorKeys[seriesIndex % colorKeys.length]]
    return { lineStyle: { color }, itemStyle: { color } }
  },
  resolveAreaStyle: () => ({
    color: thaloColors.primary.lightBlue,
    opacity: 0.3,
    origin: 3 as any,
  }),
}

const defaultSeries: SeriesTypeDefinition = {
  kind: 'default',
  matches: () => true,
  tooltipVisibility: 'show',
  skipGapDetection: false,
  symbolAlwaysHidden: false,
  resolveStyle: ({ seriesIndex }) => {
    const colorKeys = Object.keys(thaloColors.primary) as Array<keyof typeof thaloColors.primary>
    const color = thaloColors.primary[colorKeys[seriesIndex % colorKeys.length]]
    return { lineStyle: { color }, itemStyle: { color } }
  },
  resolveAreaStyle: () => ({ opacity: 0.03 }),
}

// Order matters — more specific types must appear before the default catch-all
const SERIES_TYPE_REGISTRY: SeriesTypeDefinition[] = [
  outdoorTemperature,
  trendline,
  gapBridge,
  powerOffBand,
  idealHighUp,
  idealHighDown,
  defaultSeries,
]

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns the definition for the given series name.
 * Falls through to the `default` type if no special type matches.
 */
export function resolveSeriesType(name: string): SeriesTypeDefinition {
  return SERIES_TYPE_REGISTRY.find(def => def.matches(name)) ?? defaultSeries
}
